import React, { useState, useEffect } from 'react';
import type { Workout } from '../../types';
import { DataService } from '../../services/dataService';
import { formatCardDateShort } from '../../utils/dateUtils';
import { ContextualChatButton } from '../ContextualChatButton';
import { WorkoutCardWeather } from '../WorkoutCardWeather';
import { getWorkoutIconType, WorkoutIcon } from '../../utils/workoutIconMapper';
import { DataSourceBadges } from '../badges/DataSourceBadges';
import { PushedToDeviceBadge } from '../badges/PushedToDeviceBadge';
import { getDataSources, shouldShowPushStatus, shouldShowDataSources } from '../../utils/attributionHelpers';
// Icon imports
import { CardioIcon, StrengthIcon, HIITIcon, YogaIcon, MobilityIcon } from '../icons';
// Card system styles
import '../../styles/card-system.css';

// Import Anima components
import { CmpWorkoutcard as AnimaWorkoutCard } from '../../anima/Cards/Cards-Live-Workout/src/components/CmpWorkoutcard/CmpWorkoutcard';
import { CmpWorkoutcard as AnimaArchivedWorkoutCard } from '../../anima/Cards/Cards-Archived-Workout/src/components/CmpWorkoutcard/CmpWorkoutcard';

interface SimpleWorkoutCardProps {
  workout: Workout;
  isArchived?: boolean;
  onTap?: () => void;
  onLongPress?: () => void;
  onComplete?: (workout: Workout) => void;
  showTodayBadge?: boolean;
  showDateBadge?: boolean;
}

const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : 'Morning';

export const SimpleWorkoutCard: React.FC<SimpleWorkoutCardProps> = ({ 
  workout, 
  isArchived = false, 
  onTap, 
  onLongPress, 
  onComplete, 
  showTodayBadge = false, 
  showDateBadge = false 
}) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  
  // Get workout state
  const state = DataService.getWorkoutState(workout);
  const isCompleted = state === 'completed' || state === 'done';
  
  // 🔄 JAN 22, 2026: Check for child mobility (warmup/cooldown)
  const [childMobility, setChildMobility] = useState<any>(null);
  
  useEffect(() => {
    const mobilityArray = DataService.getComponentMobilityForWorkout(workout.id);
    // If multiple children (warmup + cooldown), use first one (warmup typically comes first)
    setChildMobility(mobilityArray.length > 0 ? mobilityArray[0] : null);
  }, [workout.id]);
  
  // Get child mobility icon/color
  const getMobilityIconColor = () => {
    if (!childMobility) return { color: 'var(--cedar-primary)', Icon: MobilityIcon };
    
    const type = childMobility.type || '';
    
    // Match colors from SimpleMobilityCard.tsx
    if (type.includes('yoga')) {
      return { color: 'var(--coloryogatxt)', Icon: YogaIcon }; // Purple
    }
    if (type.includes('warmup') || type.includes('cooldown')) {
      return { color: 'var(--cedar-accent-sky)', Icon: MobilityIcon }; // Sky blue
    }
    // Default mobility color (cedar green)
    return { color: 'var(--cedar-primary)', Icon: MobilityIcon };
  };
  
  const handleComplete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onComplete) return;
    
    // 🐛 FEB 3, 2026 (Bug #74): Parse date string correctly for comparison
    // scheduledDate is now a string "2026-02-04", parse at noon UTC
    const isToday = workout.scheduledDate && (() => {
      const dateStr = typeof workout.scheduledDate === 'string' ? workout.scheduledDate : workout.scheduledDate.toISOString().split('T')[0];
      const workoutDateAtNoon = new Date(dateStr + 'T12:00:00Z');
      const todayAtNoon = new Date(new Date().toISOString().split('T')[0] + 'T12:00:00Z');
      return workoutDateAtNoon.toDateString() === todayAtNoon.toDateString();
    })();
    
    // Only allow completion if it's today's workout (or no date set for flexibility)
    if (workout.scheduledDate && !isToday) {
      return;
    }
    
    setIsCompleting(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mark as just completed for immediate visual feedback
    setJustCompleted(true);
    
    onComplete(workout);
    
    setIsCompleting(false);
    
    // Clear the justCompleted flag after DataService has had time to update
    // Check periodically if DataService state has updated (max 10 attempts = 2 seconds)
    let attempts = 0;
    const maxAttempts = 10;
    const checkStateUpdate = () => {
      attempts++;
      const newState = DataService.getWorkoutState(workout);
      if (newState === 'done' || attempts >= maxAttempts) {
        setJustCompleted(false);
      } else {
        setTimeout(checkStateUpdate, 200);
      }
    };
    setTimeout(checkStateUpdate, 500);
  };

  // Render checkmark as indicator only (not clickable). Completed: small white checkmark only (card is already dark green).
  const renderCheckmark = () => {
    if (state === 'done' || justCompleted) {
      return (
        <div className="flex items-center justify-center" title="Completed">
          <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    
    // Show grey indicator for not completed (always show for scheduled/live/ack states)
    return (
      <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center" title="Not completed">
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  };

  // Get cleaner workout type configuration with Lucide icons
  const getSimplifiedCategory = (type: string) => {
    if (type.includes('cardio') || type.includes('running') || type.includes('cycling') || type.includes('swimming')) {
      return { 
        label: 'Cardio', 
        color: 'var(--cedar-accent-sky)',
        bgColor: 'rgba(66, 165, 165, 0.1)',
        Icon: CardioIcon
      };
    }
    if (type.includes('strength') || type.includes('upper') || type.includes('lower') || type.includes('bodyweight')) {
      return { 
        label: 'Strength', 
        color: 'var(--coloryogatxt)',
        bgColor: 'rgba(120, 52, 181, 0.1)',
        Icon: StrengthIcon
      };
    }
    if (type.includes('hiit') || type.includes('sport') || type.includes('basketball') || type.includes('soccer') || type.includes('tennis')) {
      return { 
        label: 'HIIT', 
        color: 'var(--colorrehabtxt)',
        bgColor: 'rgba(201, 45, 32, 0.1)',
        Icon: HIITIcon
      };
    }
    if (type.includes('flexibility') || type.includes('yoga') || type.includes('stretch')) {
      return { 
        label: 'Yoga', 
        color: 'var(--coloryogatxt)',
        bgColor: 'rgba(120, 52, 181, 0.1)',
        Icon: YogaIcon
      };
    }
    if (type.includes('mobility')) {
      return {
        label: 'Mobility',
        color: 'var(--cedar-primary)',
        bgColor: 'rgba(31, 108, 79, 0.1)',
        Icon: MobilityIcon
      };
    }
    return { 
      label: 'Fitness', 
      color: 'var(--cedar-primary)',
      bgColor: 'rgba(31, 108, 79, 0.1)',
      Icon: CardioIcon
    };
  };

  // 🔧 CRITICAL FIX: Ensure workout.type is always a string (prevent React error #31)
  const workoutType = typeof workout.type === 'string' ? workout.type : String(workout.type ?? 'cardio');
  const workoutActivityType = typeof workout.activityType === 'string' ? workout.activityType : (workout.activityType != null ? String(workout.activityType) : undefined);
  const workoutTitle = typeof workout.title === 'string' ? workout.title : String(workout.title ?? 'Workout');
  
  const category = getSimplifiedCategory(workoutType);
  // 🐛 FEB 3, 2026 (Bug #74): Parse date string correctly
  const isToday = workout.scheduledDate && (() => {
    const dateStr = typeof workout.scheduledDate === 'string' ? workout.scheduledDate : workout.scheduledDate.toISOString().split('T')[0];
    const workoutDateAtNoon = new Date(dateStr + 'T12:00:00Z');
    const todayAtNoon = new Date(new Date().toISOString().split('T')[0] + 'T12:00:00Z');
    return workoutDateAtNoon.toDateString() === todayAtNoon.toDateString();
  })();
  
  // 🔧 FIX (Dec 6, 2025): Format duration as "55m" or "1h 22m"
  const formatDuration = (minutes: number | undefined): string => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };
  
  // 🔧 FIX (Dec 6, 2025): Get day of week from scheduled date
  const getDayOfWeek = (date: Date | string | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[d.getDay()];
  };
  
  // 🔧 FIX (Dec 6, 2025): Format subcategory for display (e.g., "running-long" -> "Running Long")
  const formatSubcategory = (type: string): string => {
    if (!type) return 'Fitness';
    return type
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Add new state for activity data
  const [hasMatchedActivity, setHasMatchedActivity] = useState(false);
  const [deviceDataSources, setDeviceDataSources] = useState<any[]>([]);

  // Check for matched activity data and device sync on mount
  useEffect(() => {
    const activity = DataService.getMatchedActivity(workout.id);
    setHasMatchedActivity(!!activity);
    
    // Check for device data sources (Garmin/Strava sync)
    // Build sources array from activity data or workout sync status
    const sources: any[] = [];
    
    if (activity) {
      // Check activity for source type
      if (activity.source === 'garmin' || activity.deviceManufacturer === 'Garmin') {
        sources.push({
          type: 'garmin',
          deviceModel: activity.deviceModel,
          deviceManufacturer: activity.deviceManufacturer || 'Garmin'
        });
      }
      if (activity.source === 'strava') {
        sources.push({ type: 'strava' });
      }
      if (activity.source === 'apple_health') {
        sources.push({ type: 'apple_health' });
      }
      if (activity.source === 'google_health') {
        sources.push({
          type: 'google_health',
          deviceModel: activity.deviceModel,
          deviceManufacturer: activity.deviceManufacturer || 'Google'
        });
      }
    } else if (workout.syncStatus === 'synced') {
      // Fallback: check workout sync status
      if (workout.watchPlatform === 'garmin') {
        sources.push({ type: 'garmin' });
      }
    }
    
    setDeviceDataSources(sources);
  }, [workout.id, workout.syncStatus, workout.watchPlatform]);

  // Determine if workout was pushed to device
  const isPushedToDevice = workout.watchPushStatus === 'pushed';
  const pushPlatform = workout.watchPlatform;

  // Determine if workout has device sync data (post-completion)
  const hasDeviceSyncData = (state === 'done' || state === 'completed') && deviceDataSources.length > 0;

  const AnimaComponent = isArchived ? AnimaArchivedWorkoutCard : AnimaWorkoutCard;

  // Enhanced shadow for browse view (when showDateBadge is true)
  const cardShadow = showDateBadge ? 'shadow-lg' : 'shadow-sm';
  // Background: completed/done → dark green (cedar-dark); else cedar-subtle. Press: completed → cedar-accent-sky, else cedar-light.
  const cardBg = isPressed
    ? (isCompleted ? 'var(--cedar-accent-sky)' : 'var(--cedar-light)')
    : (isCompleted ? 'var(--cedar-dark)' : 'var(--cedar-subtle)');

  return (
    <div className={`relative mypath-card ${cardShadow}`} style={{ minHeight: '140px', backgroundColor: cardBg }}>
      <div
        onClick={onTap}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          onLongPress && onLongPress();
        }}
        className="cursor-pointer relative"
        style={{ minHeight: '140px', paddingBottom: '52px' }}
      >
        {/* Anima component completely hidden - only used for state management */}
        <div className="absolute inset-0 opacity-0 pointer-events-none overflow-hidden" style={{ visibility: 'hidden' }}>
          <AnimaComponent
            state={state}
            text={getDayOfWeek(workout.scheduledDate)} // For 'done' state
            text1={getDayOfWeek(workout.scheduledDate)} // For 'ack' state  
            text2={getDayOfWeek(workout.scheduledDate)} // For 'live' state - shows day of week instead of "Week 2 Day 1"
            text3={workoutTitle}
          />
        </div>

        {/* HEADER ROW: Date (left) | Category (center) | Checkmark (right of category) | Chat (right of checkmark) - limestone top strip */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-center px-3 py-2 z-20 rounded-t-lg border-b" style={{ 
          backgroundColor: 'var(--cedar-limestone)', 
          borderColor: 'var(--cedar-limestone)',
          minHeight: '44px' // Ensure consistent header height
        }}>
          {/* Date - Always show on left if we have a date (Browse/Weekly/Today views) */}
          {workout.scheduledDate && (showDateBadge || showTodayBadge) ? (
            <div className="mypath-card-label font-medium px-2 py-1 rounded flex-shrink-0 flex items-center">
              {/* 🐛 FEB 3, 2026 (Bug #74): Pass string directly (no new Date conversion) */}
              {formatCardDateShort(workout.scheduledDate)}
            </div>
          ) : (
            <div className="flex-shrink-0 w-16" /> 
          )}

          {/* Category Badge - Center */}
          <div className="flex-1 flex justify-center items-center">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ 
              backgroundColor: category.bgColor, 
              color: category.color 
            }}>
              <category.Icon size={14} color={category.color} />
              {formatSubcategory(workout.type)}
            </div>
          </div>

          {/* Completion Checkmark - Right of Category */}
          <div className="flex-shrink-0 mx-2 flex items-center">
            {renderCheckmark()}
          </div>

          {/* Chat with Cedar - Right (larger, closer to corner, hover feedback) */}
          <div className="flex-shrink-0 flex items-center -mr-1">
            <ContextualChatButton
              context={{
                type: 'workout',
                id: workout.id,
                title: workoutTitle,
                description: typeof workout.description === 'string' ? workout.description : String(workout.description ?? '')
              }}
              text=""
              size="large"
              variant="clean"
              className="transition-transform duration-150 hover:scale-110"
            />
          </div>
        </div>

        {/* Inner card: cedar-light background with white content (title + footer) */}
        <div className="absolute top-11 left-0 right-0 bottom-0 z-10 rounded-b-lg" style={{ backgroundColor: 'var(--cedar-light)' }}>
        {/* TITLE SECTION: Icon (left, vertically centered in space) | Title (left-justified, dynamically sized) */}
        <div className="absolute top-0 left-0 right-0 px-3 pt-2 pb-2 z-20 bg-white" style={{ minHeight: '60px', maxHeight: '72px' }}>
          <div className="flex items-center gap-3 h-full">
            {/* Workout Icon - Left (vertically centered in container) */}
            <div className="flex-shrink-0">
              <WorkoutIcon 
                iconType={getWorkoutIconType(workoutType, workoutActivityType)} 
              />
            </div>

            {/* Title Text - Left-justified, strikethrough when completed */}
            <div className="flex-1 flex items-center py-1">
              {(() => {
                const displayTitle = workoutTitle ? workoutTitle.split(':')[0].trim() : 'Workout';
                const fontSize = displayTitle.length > 30 ? '14px' : '16px';
                return (
                  <div 
                    className="font-semibold line-clamp-2 break-words text-left"
                    style={{
                      fontSize,
                      lineHeight: '1.3',
                      color: isCompleted ? 'rgba(255,255,255,0.95)' : 'var(--cedar-ink, #0B1614)',
                      textDecoration: isCompleted ? 'line-through' : 'none'
                    }}
                  >
                    {displayTitle}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* POST-COMPLETION METRICS ROW: compact inline metrics for completed cards */}
        {isCompleted && (() => {
          const activity = DataService.getMatchedActivity(workout.id);
          if (!activity) return null;
          const metrics: Array<{label: string, value: string}> = [];
          if (activity.duration_sec && activity.duration_sec > 0) {
            const mins = Math.round(activity.duration_sec / 60);
            metrics.push({ label: 'Duration', value: mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h${mins%60 > 0 ? `${mins%60}m` : ''}` });
          }
          if (activity.distance_m && activity.distance_m > 100) {
            metrics.push({ label: 'Distance', value: `${(activity.distance_m / 1000).toFixed(1)}km` });
          }
          if (activity.calories && activity.calories > 0) {
            metrics.push({ label: 'Calories', value: `${Math.round(activity.calories)}` });
          }
          if (activity.avg_hr && activity.avg_hr > 0) {
            metrics.push({ label: 'Avg HR', value: `${Math.round(activity.avg_hr)}` });
          }
          if (metrics.length === 0) return null;
          return (
            <div className="absolute bottom-10 left-0 right-0 px-3 z-20">
              <div className="flex items-center gap-3 justify-center">
                {metrics.slice(0, 3).map((m, i) => (
                  <div key={i} className="text-center">
                    <div className="text-[10px] text-gray-400 uppercase">{m.label}</div>
                    <div className="text-xs font-semibold text-gray-500">{m.value}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* FOOTER ROW: Duration (left) | Child Mobility Icon (center) | Weather (right) */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between items-center px-3 py-2 z-20 bg-white rounded-b-lg">
          {/* Duration + Distance + Device Icons - Left */}
          <div className="flex items-center gap-2">
            {workout.duration ? (
              <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatDuration(typeof workout.duration === 'number' ? workout.duration : (typeof workout.duration === 'string' ? parseInt(workout.duration, 10) : (workout.duration_minutes || 0)))}
              </div>
            ) : (
              <div />
            )}

            {/* Distance badge for cardio workout types */}
            {(() => {
              const cardioTypes = ['cardio', 'running', 'cycling', 'swimming', 'walking'];
              const isCardio = cardioTypes.some(t => workoutType.includes(t) || (workoutActivityType && workoutActivityType.includes(t)));
              if (!isCardio) return null;
              const workoutData = workout.workoutData || workout.workout_data;
              const rawDistance = workoutData?.distance;
              if (!rawDistance) return null;
              const distValue = typeof rawDistance === 'object' && rawDistance.value != null ? rawDistance.value : (typeof rawDistance === 'number' ? rawDistance : null);
              const distUnit = typeof rawDistance === 'object' && rawDistance.unit ? rawDistance.unit : 'km';
              if (distValue == null || distValue <= 0) return null;
              return (
                <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {typeof distValue === 'number' ? (Number.isInteger(distValue) ? distValue : distValue.toFixed(1)) : distValue}{distUnit}
                </div>
              );
            })()}

            {/* Device Icons: Show for BOTH pre-completion (pushed) AND post-completion (synced) */}
            {/* Post-completion: Device sync badges (Garmin/Strava data received) */}
            {hasDeviceSyncData && (
              <div className="flex items-center gap-1">
                <DataSourceBadges sources={deviceDataSources} size="small" />
              </div>
            )}

            {/* Pre-completion: Pushed to device badge (workout sent to watch) */}
            {!hasDeviceSyncData && isPushedToDevice && pushPlatform && (
              <div className="flex items-center">
                <PushedToDeviceBadge 
                  platform={pushPlatform === 'apple_watch' ? 'apple_watch' : 'garmin'}
                  deviceModel={workout.watchWorkoutId ? undefined : undefined}
                  size="small"
                />
              </div>
            )}
          </div>

          {/* 🔄 JAN 23, 2026: Child Mobility Indicator - Thin outline circle with colored icon */}
          {childMobility && (() => {
            const { color, Icon } = getMobilityIconColor();
            return (
              <div 
                className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center"
                onClick={onTap}
                style={{ cursor: 'pointer' }}
              >
                <div 
                  className="rounded-full flex items-center justify-center bg-white"
                  style={{
                    width: '24px',
                    height: '24px',
                    border: `1px solid ${color}`
                  }}
                >
                  <Icon size={14} color={color} />
                </div>
              </div>
            );
          })()}

          {/* Weather - Right (show for outdoor workout types) */}
          {(() => {
            const outdoorTypes = ['running', 'walking', 'cycling', 'hiking'];
            const workoutData = workout.workoutData || workout.workout_data;
            const isOutdoor = workoutData?.is_outdoor || outdoorTypes.some(t => workoutType.includes(t) || (workoutActivityType && workoutActivityType.includes(t)));
            if (!workout.scheduledDate || !isOutdoor) return <div />;
            return (
              <div className="flex-shrink-0">
                <WorkoutCardWeather
                  workoutDate={workout.scheduledDate}
                  size="sm"
                />
              </div>
            );
          })()}
        </div>
        </div>

        {/* Status Badge - if needed (overlay) */}
        {workout.status === 'missed' && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium z-20">
            Missed
          </div>
        )}

        {/* Today Badge - if needed (overlay) */}
        {showTodayBadge && isToday && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium z-20">
            Today
          </div>
        )}
      </div>
    </div>
  );
}; 